// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.FilterDetails.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Filter Details model
 */

import setValue from 'share/utils/Izenda.setValue';
import { observable, action, computed } from 'mobx';
import tracker from 'share/tracking/Izenda.Tracker';
import constants, { CALCULATED_FIELD_NAME, FIELD_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';
import uiConstants from 'logic/data/Izenda.UIConstants';
import ObjectStateConstants from 'logic/data/Izenda.ObjectStateConstants';
import { get, remove, uniqueId, getOr, isEqual, isNull } from 'lodash/fp';
import { FILTER_LOOKUP_STATUSES, FILTER_LOOKUP_TYPES } from 'logic/data/Izenda.FilterLookupConstants';
import { EXCLUDE_FILTER_DATA_TYPE } from 'logic/data/Izenda.FilterDetailsConfigConstants';

const defaultFontFormat = {
  fontFamily: uiConstants.defaultFontFamily,
  fontSize: uiConstants.defaultFontSize || uiConstants.fontSize[0]
};

@tracker
export default class FilterDetails {
  @observable connectionName = '';
  @observable querySourceCategoryName = '';
  @observable sourceFieldName = '';
  @observable sourceFieldVisible = true;
  @observable sourceFieldFilterable = true;
  @observable sourceDataObjectName = '';
  @observable dataType = '';
  @observable filterId = null;
  @observable querySourceFieldId = null;
  @observable querySourceType = '';
  @observable querySourceId = null;
  @observable relationshipId = null;
  @observable alias = '';
  @observable position = 0;
  @observable visible = true;
  @observable required = false;
  @observable cascading = false;
  @observable operatorId = null;
  @observable operatorName = '';
  @observable operatorSetting = '';
  @observable value = '';
  @observable defaultValue = '';
  @observable sortType = null;
  @observable fontFamily = defaultFontFormat.fontFamily;
  @observable fontSize = defaultFontFormat.fontSize;
  @observable textColor = uiConstants.textColor;
  @observable backgroundColor = uiConstants.backgroundColor;
  @observable fontBold = false;
  @observable fontItalic = false;
  @observable fontUnderline = false;
  @observable id = null;
  @observable state = 0;
  @observable modified = '';
  @observable dateTimeNow = '';
  @observable selected = false;
  @observable reportPartTitle = null;
  @observable reportFieldAlias = null;
  @observable dataFormatId = null;
  @observable dataFormat = null;
  @observable isInheritableFilter = false;
  @observable filterLookupType = FILTER_LOOKUP_TYPES.NONE;
  @observable filterLookupStatus = FILTER_LOOKUP_STATUSES.VALID;
  @observable useLookup = true;
  @observable isPredicated = false;
  @observable targetValue = null;
  @observable modelName = null;
  @observable relationColumn = null;
  // UI properties
  @observable isParameter = false;
  @observable isForKeyJoin = false;
  @observable isDisableCascading = false;
  commonFilterRequired = false;

  // Mark if required filter is not applied yet
  @observable requiredFilterNotApply = false;
  @computed
  get sourceDataObjectFullName() {
    return `{${this.connectionName}}.{${this.querySourceCategoryName}}.{${this.sourceDataObjectName}}`;
  }

  @computed
  get hasUserDefinedLookup() {
    return this.filterLookupType === FILTER_LOOKUP_TYPES.USER_DEFINED;
  }

  @computed
  get hasValidLookupKey() {
    return (
      this.filterLookupType === FILTER_LOOKUP_TYPES.LOOKUP_KEY &&
      this.filterLookupStatus === FILTER_LOOKUP_STATUSES.VALID
    );
  }

  @computed
  get showUseLookupCheckbox() {
    return this.hasValidLookupKey && !this.isParameter && !this.isForKeyJoin;
  }

  @computed
  get displayable() {
    return (
      this.dataType != EXCLUDE_FILTER_DATA_TYPE.REF_CURSOR &&
      this.dataType != EXCLUDE_FILTER_DATA_TYPE.SYS_REFCURSOR &&
      this.state !== constants.OBJECT_STATE.DELETE
    );
  }

  constructor(data, filterList, markUpdateResult) {
    this.markUpdateResult = markUpdateResult;
    this.filterList = filterList || [];
    this.filterItemDom = {};

    // unique Id for Filter Body. using this id as key will prevent component
    // from unmount and willMount new component thus keep the validation status
    if (get('uniqueId', data)) {
      this.uniqueId = data.uniqueId;
    } else {
      this.uniqueId = uniqueId();
    }

    data && this.setFilterDetails(data);
    this.cleanDirty();
  }

  /**
   * Set data source for filter
   * @param {Object} data Source Data
   * @param {Boolean} isUpdate
   */
  @action
  setDataSource(data: any, isUpdate: boolean): void {
    const isCalculatedField = data.querySourceType === CALCULATED_FIELD_NAME;

    setValue(this, 'connectionName', data.connectionName);
    setValue(this, 'querySourceCategoryName', data.querySourceCategoryName);
    setValue(this, 'sourceFieldName', data.sourceFieldName);
    setValue(this, 'sourceDataObjectName', isCalculatedField ? CALCULATED_FIELD_NAME : data.sourceDataObjectName);
    setValue(this, 'dataType', data.dataType);
    !isUpdate && setValue(this, 'filterId', data.filterId);
    setValue(this, 'querySourceFieldId', data.querySourceFieldId);
    setValue(this, 'querySourceId', data.querySourceId);
    setValue(this, 'relationshipId', data.relationshipId);
    setValue(this, 'querySourceType', data.querySourceType);
    setValue(this, 'sourceFieldVisible', data.sourceFieldVisible);

    setValue(this, 'sourceFieldFilterable', data.sourceFieldFilterable);
    setValue(this, 'reportPartTitle', data.reportPartTitle);
    setValue(this, 'reportFieldAlias', data.reportFieldAlias);

    this.markDirty();
    this.markUpdateResult && this.markUpdateResult();
  }
  /**
   * Set filter setting
   * @param {Object} data
   */
  @action
  setFilterSettings(data: any): void {
    const alias = String.generateNewName(
      data.sourceFieldName,
      remove(obj => this.alias === obj.alias, this.filterList),
      get('alias')
    );

    // if alias is null, it will be value of sourceFieldName
    setValue(this, 'alias', data.alias || alias);
    setValue(this, 'visible', data.visible);
    setValue(this, 'required', data.required);
    setValue(this, 'cascading', data.cascading);
    setValue(this, 'operatorId', data.operatorId);
    setValue(this, 'operatorName', data.operatorName);
    setValue(this, 'operatorSetting', data.operatorSetting);
    setValue(this, 'value', this.resolveValueForUpdating(data.value));
    setValue(this, 'defaultValue', this.resolveValueForUpdating(data.value));
    setValue(this, 'sortType', data.sortType);

    data.position && setValue(this, 'position', data.position);
    this.markDirty();
    this.markUpdateResult && this.markUpdateResult();
  }

  /**
   * Set filter lookup
   * @param {Object} data
   */
  @action
  setFilterLookup(data: any): void {
    setValue(this, 'filterLookupType', getOr(FILTER_LOOKUP_TYPES.NONE, 'filterLookupType', data));
    setValue(this, 'filterLookupStatus', getOr(FILTER_LOOKUP_STATUSES.VALID, 'filterLookupStatus', data));
    setValue(this, 'useLookup', !this.showUseLookupCheckbox || getOr(true, 'useLookup', data));

    this.markDirty();
    this.markUpdateResult && this.markUpdateResult();
  }

  /**
   * Set format for filter
   * @param {Object} data
   */
  @action
  setFilterFormat(data: any): void {
    setValue(this, 'fontFamily', data.fontFamily || defaultFontFormat.fontFamily);
    setValue(this, 'fontSize', data.fontSize || defaultFontFormat.fontSize);
    setValue(this, 'textColor', data.textColor);
    setValue(this, 'backgroundColor', data.backgroundColor);
    setValue(this, 'fontBold', data.fontBold);
    setValue(this, 'fontItalic', data.fontItalic);
    setValue(this, 'fontUnderline', data.fontUnderline);
    setValue(this, 'dataFormatId', data.dataFormatId);
    setValue(this, 'dataFormat', data.dataFormat || null);
    setValue(this, 'commonFilterRequired', data.commonFilterRequired);
    this.markDirty();
    this.markUpdateResult && this.markUpdateResult();
  }

  /**
   * Set common data of filter List
   * @param {Object} data
   * @param {Boolean} isUpdate
   */
  @action
  setCommon(data: any, isUpdate: boolean): void {
    if (this.state === ObjectStateConstants.DELETE) return;
    !isUpdate && setValue(this, 'id', data.id);
    setValue(this, 'state', isUpdate ? ObjectStateConstants.UPDATE : data.state);
    setValue(this, 'modified', data.modified);
    setValue(this, 'dateTimeNow', data.dateTimeNow);
    // UI properties
    setValue(this, 'isParameter', data.isParameter);
    setValue(this, 'isForKeyJoin', data.isForKeyJoin);
    setValue(this, 'isPredicated', data.isPredicated);
    setValue(this, 'targetValue', data.targetValue);
    setValue(this, 'modelName', data.modelName);
    setValue(this, 'relationColumn', data.relationColumn);
    setValue(this, 'isInheritableFilter', data.isInheritableFilter);
    this.markDirty();
    this.markUpdateResult && this.markUpdateResult();
  }

  /**
   * Set filter details
   * @param {Object} data
   * @param {Boolean} isUpdate
   */
  @action
  setFilterDetails(data: any, isUpdate: boolean): void {
    this.setCommon(data, isUpdate);
    this.setDataSource(data, isUpdate);
    this.setFilterSettings(data);
    this.setFilterLookup(data);
    this.setFilterFormat(data);
  }

  /**
   * Update FilterDetails properties
   * @param {String} key
   * @param {Object} value
   * @returns {boolean} Indicates if the properties is update successfully
   */
  @action
  updateByKey(key: string, value: any): void {
    if (this.state === ObjectStateConstants.DELETE) return false;
    if (isEqual(get(key, this), value)) return false;

    // Set requiredFilterNotApply to true flag when visible required filter is changed from non-selected
    if (!get(key, this) && this.required && this.visible) {
      this.requiredFilterNotApply = true;
    }

    setValue(this, key, value);
    this.state !== ObjectStateConstants.INSERT && setValue(this, 'state', ObjectStateConstants.UPDATE);
    this.markDirty();
    this.markUpdateResult && this.markUpdateResult();

    return true;
  }

  /**
   * Update Operator Details
   * @param {Object} data
   */
  @action
  updateOperatorDetail(data: any): void {
    data = data || {};

    setValue(this, 'operatorId', data.operatorId);
    setValue(this, 'operatorName', data.operatorName);
    setValue(this, 'operatorSetting', data.operatorSetting);
    setValue(this, 'value', this.resolveValueForUpdating(data.value));

    this.markDirty();
    this.markUpdateResult && this.markUpdateResult();
  }

  /**
   * Get dirty status.
   */
  checkDirty(): boolean {
    return this.isDirty;
  }

  /**
   * Clean dirty status.
   */
  cleanDirty(): void {
    this.resetDirty();
  }

  /**
   * Get Filter Details
   */
  getFilterDetails(): void {
    return {
      connectionName: this.connectionName,
      querySourceCategoryName: this.querySourceCategoryName,
      sourceFieldName: this.sourceFieldName,
      sourceFieldVisible: this.sourceFieldVisible,
      sourceFieldFilterable: this.sourceFieldFilterable,
      sourceDataObjectName: this.sourceDataObjectName,
      dataType: this.dataType,
      filterId: this.filterId,
      querySourceFieldId: this.querySourceFieldId,
      querySourceType: this.querySourceType,
      querySourceId: this.querySourceId,
      relationshipId: this.relationshipId,
      alias: this.alias,
      position: this.position,
      visible: this.visible,
      required: this.required,
      cascading: this.cascading,
      operatorId: this.operatorId,
      operatorName: this.operatorName,
      operatorSetting: this.operatorSetting,
      value: this.value,
      defaultValue: this.defaultValue,
      sortType: this.sortType,
      fontFamily: this.fontFamily,
      fontSize: this.fontSize,
      textColor: this.textColor,
      backgroundColor: this.backgroundColor,
      fontBold: this.fontBold,
      fontItalic: this.fontItalic,
      fontUnderline: this.fontUnderline,
      id: this.id,
      state: this.state,
      modified: this.modified,
      dateTimeNow: this.dateTimeNow,
      isParameter: this.isParameter,
      isForKeyJoin: this.isForKeyJoin,
      isPredicated: this.isPredicated,
      targetValue: this.targetValue,
      modelName: this.modelName,
      relationColumn: this.relationColumn,
      sourceDataObjectFullName: this.sourceDataObjectFullName,
      selected: this.selected,
      dataFormatId: this.dataFormatId,
      reportPartTitle: this.reportPartTitle,
      reportFieldAlias: this.reportFieldAlias,
      uniqueId: this.uniqueId,
      isInheritableFilter: this.isInheritableFilter,
      useLookup: this.useLookup
    };
  }

  /**
   * Update operator setting.
   * @param  {Object} value
   */
  setOperatorSetting(value): void {
    this.updateByKey('operatorSetting', value);
  }

  /**
   * Get unique filter id
   */
  getFilterId(): void {
    const querySourceFieldId = this.querySourceType === FIELD_TYPE.AggregatedField ? '' : this.querySourceFieldId;
    return this.reportPartTitle
      ? querySourceFieldId + this.reportPartTitle + this.reportFieldAlias
      : querySourceFieldId && this.sourceDataObjectName
      ? querySourceFieldId + this.sourceDataObjectName
      : null;
  }

  /**
   * Get display style
   */
  getDisplayStyle(): void {
    return {
      fontFamily: this.fontFamily,
      fontSize: this.fontSize,
      fontWeight: this.fontBold ? 'bold' : 'normal',
      fontStyle: this.fontItalic ? 'italic' : 'normal',
      textDecoration: this.fontUnderline ? 'underline' : 'none',
      color: this.textColor,
      backgroundColor: this.backgroundColor
    };
  }

  /**
   * Set disable cascading
   * @returns {boolean} The value indicate if the cascading has been changed
   */
  @action
  setDisableCascading(value: boolean): void {
    this.isDisableCascading = value;
    if (value && this.cascading) {
      // Mark dirty when cascading change
      this.markDirty();
      this.cascading = false;
      return true;
    }
    return false;
  }

  /**
   * Resolve value data for updating
   * @param {String} source
   */
  resolveValueForUpdating(source: string) {
    return isNull(source) ? '' : source;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.FilterDetails.js